<template>
  <div style="width: 100%">
    <v-row align="center" style="max-height: 56px">
      <v-col cols="auto">
        <div class="title text--secondary text-uppercase">
          {{ $t("mentorship.actions.management.title") }}
        </div>
      </v-col>
      <v-col class="text-end">
        <v-btn
          v-if="role('admin', 'safetyRepresentative', 'departmentManager')"
          class="primary"
          @click="add"
        >
          <v-icon dark left>note_add</v-icon>
          {{ $t("mentorship.actions.management.add") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      class="safety-elevation-3 table-striped"
      :headers="headers"
      :items="mentorshipStats"
      :search="search"
      sort-by="mentorshipAction.version.created"
      :sort-desc="true"
      :items-per-page="5"
    >
      <template v-slot:top>
        <v-row justify="end" class="ma-3">
          <v-text-field
            v-model="search"
            :label="$t('search.title')"
            class="flex-grow-1 pt-3 mr-3"
            prepend-inner-icon="search"
            single-line
            :background-color="
              $vuetify.theme.themes.light.primary | hexToRgb(0.2)
            "
            solo
            dense
            flat
            hide-details
            clearable
          />

          <!-- <v-btn
            :text="!showActive"
            color="primary"
            class="ml-3 mt-3"
            @click="showActive = true"
            >{{ $t("instructions.tabs.active") }}
          </v-btn>
          <v-btn
            :text="showActive"
            color="primary"
            class="mt-3"
            @click="showActive = false"
            >{{ $t("instructions.tabs.inactive") }}
          </v-btn> -->
        </v-row>
      </template>

      <template v-slot:[`item.name`]="{ item }">
        <a
          v-if="role('admin', 'safetyRepresentative', 'departmentManager')"
          @click="edit(item)"
          style="text-decoration: underline;"
          >{{ item.name }}</a
        >
        <span v-else>{{ item.name }}</span>
      </template>

      <template v-slot:[`item.tagText`]="{ item }">
        <v-chip small> {{ item.tagText }} </v-chip>
      </template>

      <template v-slot:[`item.mentorshipAction.version.created`]="{ item }">
        <span v-if="item.mentorshipAction">
          {{ item.mentorshipAction.version.created | date }}
        </span>
      </template>

      <template v-slot:[`item.contentsByLanguage`]="{ item }">
        <template v-if="item.contentsByLanguage">
          <v-chip
            small
            v-for="[language] in item.contentsByLanguage"
            :key="language"
          >
            <router-link
              class="text-uppercase "
              :to="{
                name: 'mentorship-actions',
                params: { id: item.tag.id },
                query: {
                  language: language,
                  revision: item.mentorshipAction.version.revision,
                },
              }"
              target="_blank"
            >
              {{ language }}
            </router-link>
          </v-chip>
        </template>
      </template>
    </v-data-table>
    <mentorship-action-edit
      v-if="editTagId != null"
      :tagId="Number(editTagId)"
      @done="done"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import MentorshipStatsQuery from "@/graphql/queries/MentorshipStats.gql";
import MentorshipActionEdit from "@/components/management/MentorshipActionEdit.vue";

import { getTagText } from "@/utils/mentorshipActions.js";

export default {
  components: {
    MentorshipActionEdit,
  },
  data() {
    return {
      search: "",
      showActive: true,
      editTagId: null,
    };
  },
  computed: {
    ...mapGetters(["role"]),

    headers() {
      const h = [
        {
          text: this.$i18n.t("mentorship.actions.column.code"),
          value: "mentorshipAction.code",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("mentorship.actions.column.name"),
          value: "name",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("mentorship.actions.column.revision"),
          value: "mentorshipAction.version.revision",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("mentorship.actions.column.date"),
          value: "mentorshipAction.version.created",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("mentorship.actions.column.content"),
          value: "contentsByLanguage",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("mentorship.actions.column.tag"),
          value: "tagText",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("mentorship.actions.column.activeMentorshipCount"),
          value: "activeMentorshipCount",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t(
            "mentorship.actions.column.finishedMentorshipCount"
          ),
          value: "finishedMentorshipCount",
          class: "text-uppercase subheading text-truncate",
        },
      ];

      return h;
    },
  },
  methods: {
    add() {
      this.editTagId = 0;
    },
    edit(item) {
      this.editTagId = item.mentorshipAction.tagId;
    },
    done(updated) {
      if (updated) {
        this.$apollo.queries.mentorshipStats.refetch();
      }
      this.editTagId = null;
    },

    getContentsByLanguage(contents) {
      const map = new Map();
      contents.forEach((c) => {
        if (map.has(c.language)) {
          map.get(c.language).push(c);
        } else {
          map.set(c.language, [c]);
        }
      });
      return map;
    },
  },
  apollo: {
    mentorshipStats: {
      query: MentorshipStatsQuery,
      variables() {
        return {
          loadActiveTags: this.showActive,
        };
      },
      update(data) {
        return data.mentorshipStats.map((el) => {
          const name =
            (el.mentorshipAction && el.mentorshipAction.version.name.name) ||
            "";

          return {
            ...el,
            name,
            activeMentorshipCount: el.activeMentorshipIds.length,
            finishedMentorshipCount: el.finishedMentorshipIds.length,
            contentsByLanguage:
              (el.mentorshipAction &&
                this.getContentsByLanguage(
                  el.mentorshipAction.version.content
                )) ||
              null,
            tagText: getTagText(el.tag),
            search: [
              // name,
              el.code,
              el.tag.description,
              el.tag.category,
            ]
              .join("|")
              .toLowerCase(),
          };
        });
      },
    },
  },
};
</script>
